import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ChatContainer, MessagesContainer, InputContainer, Input, SendButton, LoadingIndicator, MessageText, MessageTime, MessageBubble } from '../styles/styled';
import { Message } from '../types';
import * as api from '../services/api';
import ChatSidebar from './ChatSidebar';
import { theme } from '../styles/theme';
import { sanitizeMessage } from '../utils/authErrors';

const Chat: React.FC = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);

  // Chat Management State
  const [chats, setChats] = useState<any[]>([]);
  const [sidebarLoading, setSidebarLoading] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Load chat list when user logs in
  useEffect(() => {
    if (user) {
      loadChats();
    } else {
      // Guest mode: load local session if any
      const localId = api.getLocalSessionId();
      setCurrentSessionId(localId);
      loadHistory(localId);
    }
  }, [user]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadChats = async () => {
    setSidebarLoading(true);
    try {
      const userChats = await api.getChats();
      setChats(userChats);

      if (userChats.length > 0 && !currentSessionId) {
        // Load most recent chat by default
        const mostRecent = userChats[0];
        setCurrentSessionId(mostRecent.id);
        loadHistory(mostRecent.id);
      } else if (userChats.length === 0) {
        // Creating a first chat automatically or waiting for user?
        // Let's wait for user to hit "New Chat" or just send a message (which creates one)
        // Actually, better UX: Create one if none exist? No, let's show empty state.
        setCurrentSessionId(null);
        setMessages([]);
      }
    } catch (err) {
      console.error("Failed to load chats", err);
    } finally {
      setSidebarLoading(false);
    }
  };

  const loadHistory = async (sessionId: string) => {
    try {
      // If switching sessions, clear current messages first to avoid flash
      setMessages([]);
      const history = await api.getChatHistory(sessionId);
      // Map backend history to frontend Message type if needed
      // Backend: { role, content } -> Frontend: { role, content, timestamp? }
      setMessages(history.map((m: any) => ({
        ...m,
        timestamp: m.createdAt ? new Date(m.createdAt) : new Date() // Ensure Date type
      })));
    } catch (err) {
      console.error("Failed to load history", err);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    if (user && !currentSessionId) {
      // If user is logged in but has no selected chat (e.g. empty state), create one first
      try {
        const newChat = await api.createChat();
        setCurrentSessionId(newChat.id);
        setChats([newChat, ...chats]); // Optimistic update
        // Now proceed to send message to THIS chat
        sendMessageInternal(input, newChat.id);
      } catch (err: any) {
        alert(sanitizeMessage(err.response?.data?.error, 'Failed to create new chat.'));
        return;
      }
    } else {
      sendMessageInternal(input, currentSessionId || undefined);
    }
  };

  const sendMessageInternal = async (content: string, sessionId?: string) => {
    const userMessage: Message = {
      role: 'user',
      content: content,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await api.sendMessage(
        // Send context of recent messages for continuity?
        // Usually we send the whole history or just new message. 
        // The API logic seems to handle "messages" array -> implying stateless or full history. 
        // Our backend `updateConversationHistory` appends. 
        // Checking server.ts: it takes `messages: ChatMessage[]`. 
        // So we should send the CONTEXT (e.g. last 10 messages + new one).
        [...messages.slice(-10), userMessage], // Sending context window
        sessionId
      );

      const botMessage: Message = {
        role: 'assistant',
        content: response.message.content,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, botMessage]);

      // If we were guest and got a session ID back, or just confirming
      if (!currentSessionId && response.sessionId) {
        setCurrentSessionId(response.sessionId);
      }

    } catch (error) {
      console.error('Error sending message:', error);
      // Add error message
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "Sorry, I'm having trouble connecting right now. 💋",
        isError: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewChat = async () => {
    try {
      const newChat = await api.createChat();
      // Backend returns { id, created: true }
      // We need title/dates for the list. Use defaults for now.
      const fullChatObj = { id: newChat.id, title: "New Chat", updatedAt: new Date() };

      setChats([fullChatObj, ...chats]);
      setCurrentSessionId(newChat.id);
      setMessages([]);
    } catch (err: any) {
      alert(sanitizeMessage(err.response?.data?.error, 'Could not create chat.'));
    }
  };

  const handleDeleteChat = async (chatId: string) => {
    try {
      await api.deleteChat(chatId);
      const updatedChats = chats.filter(c => c.id !== chatId);
      setChats(updatedChats);

      if (currentSessionId === chatId) {
        if (updatedChats.length > 0) {
          setCurrentSessionId(updatedChats[0].id);
          loadHistory(updatedChats[0].id);
        } else {
          setCurrentSessionId(null);
          setMessages([]);
        }
      }
    } catch (err) {
      console.error("Failed to delete chat", err);
    }
  };

  const handleSelectChat = (chatId: string) => {
    if (chatId === currentSessionId) return;
    setCurrentSessionId(chatId);
    loadHistory(chatId);
    if (window.innerWidth <= 768) setShowSidebar(false); // Auto close on mobile
  };

  const handleRenameChat = async (chatId: string, title: string) => {
    const previous = chats;
    // Optimistic update so the sidebar reflects the rename instantly.
    setChats(prev => prev.map(c => (c.id === chatId ? { ...c, title } : c)));
    try {
      await api.renameChat(chatId, title);
    } catch (err: any) {
      console.error('Rename failed', err);
      setChats(previous);
      alert(sanitizeMessage(err?.response?.data?.error, 'Rename failed.'));
    }
  };

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {user && (
        <ChatSidebar
          chats={chats}
          activeChatId={currentSessionId}
          onNewChat={handleNewChat}
          onSelectChat={handleSelectChat}
          onDeleteChat={handleDeleteChat}
          onRenameChat={handleRenameChat}
          loading={sidebarLoading}
        />
      )}

      <ChatContainer>
        <MessagesContainer>
          {messages.map((msg, index) => (
            <MessageBubble key={index} isUser={msg.role === 'user'}>
              <MessageText>{msg.content}</MessageText>
              {msg.timestamp && (
                <MessageTime>
                  {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </MessageTime>
              )}
            </MessageBubble>
          ))}
          {isLoading && (
            <div style={{ alignSelf: 'flex-start' }}>
              <LoadingIndicator>
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </LoadingIndicator>
            </div>
          )}
          <div ref={messagesEndRef} />
        </MessagesContainer>

        <InputContainer onSubmit={handleSendMessage}>
          <Input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Type a message..."
            disabled={isLoading}
          />
          <SendButton type="submit" disabled={isLoading || !input.trim()}>
            Send
          </SendButton>
        </InputContainer>
      </ChatContainer>
    </div>
  );
};

export default Chat;